/**
 * Toolbar - Web Version
 *
 * Top navigation bar adapted for web browser environment.
 * Simplified from desktop version - no window controls, adapted for browser.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import {
  Bug,
  ChevronLeft,
  ListChecks,
  LogOut,
  MessageSquare,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Settings,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth';
import { usePlatform } from '../contexts/platform-context';
import { cn } from '../utils/cn';
import { toolbarButtonVariants } from './toolbar-button';
import { HealthStatusIndicator } from './health-status-indicator';
import { Logo } from './icons';
import { Tooltip } from './tooltip';
import { UpdateButton } from './update-button';

const logger = createLogger('web:component:toolbar');

export type ToolbarProps = React.HTMLAttributes<HTMLElement> & {
  // Sidebar toggle
  showSidebar?: boolean;
  onToggleSidebar?: () => void;
  // Settings
  onSettings?: () => void;
  // Bug report
  onBugReport?: () => void;
  // Tasks (Enter RCM worklist)
  onTasks?: () => void;
  // New chat (replaces sign out in sidebar/extension mode)
  onNewChat?: () => void;
  // Current view context for smart toggle behavior
  currentView?: 'chat' | 'settings' | 'tasks' | null;
};

export const Toolbar = React.forwardRef<HTMLElement, ToolbarProps>(
  (
    {
      className,
      showSidebar = false,
      onToggleSidebar,
      onSettings,
      onBugReport,
      onTasks,
      onNewChat,
      currentView = 'chat',
      ...props
    },
    ref
  ) => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const { isSidebar } = usePlatform();

    // Handle sign out
    const handleSignOut = React.useCallback(async () => {
      try {
        await logout();
      } catch (error) {
        logger.error('Failed to sign out:', error);
      }
    }, [logout]);

    // Sidebar toggle — label changes based on whether we're in chat vs settings view
    const sidebarToggleLabel =
      currentView === 'chat'
        ? isSidebar
          ? showSidebar
            ? 'Hide chat history'
            : 'Chat history'
          : showSidebar
            ? 'Hide Sidebar'
            : 'Show Sidebar'
        : 'Back to Chat';

    // The panel toggle is the primary nav action — it lives at the far right so it's
    // always in the same thumb-reachable spot regardless of how many other icons are shown.
    const panelToggle = onToggleSidebar && (
      <Tooltip content={sidebarToggleLabel} side="left" delay={300}>
        <button
          type="button"
          onClick={onToggleSidebar}
          className={toolbarButtonVariants({
            tone: 'primary',
            active: showSidebar && currentView === 'chat',
          })}
          aria-label={sidebarToggleLabel}
          aria-pressed={showSidebar && currentView === 'chat'}
          data-testid="toolbar-sidebar-toggle"
        >
          {currentView === 'chat' ? (
            showSidebar ? (
              <PanelLeftClose />
            ) : (
              <PanelLeft />
            )
          ) : (
            <MessageSquare />
          )}
        </button>
      </Tooltip>
    );

    const renderControls = () => (
      <div className="flex items-center gap-0.5">
        {/* Update button and health status — not needed in the sidebar (popup has these) */}
        {!isSidebar && <UpdateButton />}
        {!isSidebar && <HealthStatusIndicator />}

        {/* Workspace controls — kept together: bug report + settings. */}
        {!isSidebar && onBugReport && (
          <Tooltip content="Report a Bug" side="left" delay={300}>
            <button
              type="button"
              onClick={onBugReport}
              className={toolbarButtonVariants()}
              aria-label="Report a bug"
              data-testid="toolbar-bug-report"
            >
              <Bug />
            </button>
          </Tooltip>
        )}

        {onTasks && (
          <Tooltip content="Tasks" side="left" delay={300}>
            <button
              type="button"
              onClick={onTasks}
              className={toolbarButtonVariants({
                tone: 'primary',
                active: currentView === 'tasks',
              })}
              aria-label="Tasks"
              aria-pressed={currentView === 'tasks'}
              data-testid="toolbar-tasks"
            >
              <ListChecks />
            </button>
          </Tooltip>
        )}

        {onSettings && (
          <Tooltip
            content={
              currentView === 'settings'
                ? isSidebar
                  ? 'Back to Chat'
                  : showSidebar
                    ? 'Hide Settings Sidebar'
                    : 'Show Settings Sidebar'
                : 'Settings'
            }
            side="left"
            delay={300}
          >
            <button
              type="button"
              onClick={onSettings}
              // Mirror the chat panel toggle: when the settings layout is open
              // with its sidebar enabled, mark it active with the blue accent.
              className={toolbarButtonVariants({
                tone: 'primary',
                active: showSidebar && currentView === 'settings',
              })}
              aria-label={currentView === 'settings' ? 'Toggle settings sidebar' : 'Settings'}
              aria-pressed={showSidebar && currentView === 'settings'}
              data-testid="toolbar-settings"
            >
              {currentView === 'settings' ? (
                isSidebar ? (
                  <ChevronLeft />
                ) : showSidebar ? (
                  <PanelLeftClose />
                ) : (
                  <PanelLeft />
                )
              ) : (
                <Settings />
              )}
            </button>
          </Tooltip>
        )}

        {isSidebar ? (
          <>
            {/* Sidebar/extension mode (unchanged): "New Chat" stays in the cluster
                and the chat/sidebar toggle stays the far-right primary nav. */}
            {onNewChat && (
              <Tooltip content="New Chat" side="left" delay={300}>
                <button
                  type="button"
                  onClick={onNewChat}
                  className={toolbarButtonVariants()}
                  aria-label="New chat"
                  data-testid="toolbar-new-chat"
                >
                  <Plus />
                </button>
              </Tooltip>
            )}
            {panelToggle && (
              <>
                <div className="w-px h-4 bg-border mx-0.5" />
                {panelToggle}
              </>
            )}
          </>
        ) : (
          <>
            {/* Full web: the chat/sidebar toggle joins the workspace controls… */}
            {panelToggle}
            {/* …and logout becomes the final isolated action, separated from the
                workspace controls by a divider. Calm at rest, then on hover it
                gets the same tinted-accent treatment as the active chat/settings
                toggles — just danger-coded in red instead of the blue accent. */}
            <div className="w-px h-4 bg-border mx-0.5" />
            <Tooltip content="Sign Out" side="left" delay={300}>
              <button
                type="button"
                onClick={handleSignOut}
                className={toolbarButtonVariants({ tone: 'danger' })}
                aria-label="Sign out"
                data-testid="toolbar-signout"
              >
                <LogOut />
              </button>
            </Tooltip>
          </>
        )}
      </div>
    );

    return (
      <header
        ref={ref}
        className={cn(
          'flex items-center w-full transition-all duration-300',
          'bg-background/95 backdrop-blur-sm border-b border-border z-1',
          isSidebar ? 'h-10 px-2' : 'h-14 px-2 sm:px-4',
          className
        )}
        {...props}
      >
        <div className="flex h-full w-full items-center justify-between">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Logo />
          </div>
          <div className="flex items-center flex-shrink-0">{renderControls()}</div>
        </div>
      </header>
    );
  }
);

Toolbar.displayName = 'Toolbar';
